import client from '@/services/http/client'
import { isMockEnabled, mockDelay, paginate, sortByParam } from '@/services/http/mock'
import { useAuthStore } from '@/stores/authStore'
import type { PaginatedData } from '@/types/api'
import type { TicketItem, TicketDetail, TicketStats } from '@/types/ticket'
import { normalizeMessageMedia } from './messages'

// ============== Mock 数据 ==============
const mockTickets: TicketItem[] = [
  {
    mapping_id: 'map-001',
    draft_no: 'DF-20260514-003',
    zentao_task_id: 12345,
    task_title: '排查-打印机E-500错误',
    is_out_of_warranty: false,
    customer_name: '某医院',
    platform_status: 'waiting_customer_confirm',
    zentao_status: 'done',
    assignee: 'tech_a',
    assigned_to: 'tech_a',
    tech_owner: 'tech_a',
    service_owner: 'svc_a',
    next_action_hint: '请同步客户确认',
    last_synced_at: '2026-05-14T11:20:00+08:00',
    task_created_at: '2026-05-14T10:30:00+08:00',
    task_finished_at: '2026-05-14T11:20:00+08:00',
    task_closed_at: '',
    sla_risk_level: 'warning',
    trigger_source_type: '',
  },
  {
    mapping_id: 'map-002',
    draft_no: 'DF-20260513-001',
    zentao_task_id: 12340,
    task_title: '修复-数据库连接超时',
    is_out_of_warranty: false,
    customer_name: '某客户',
    platform_status: 'doing',
    zentao_status: 'doing',
    assignee: 'tech_b',
    assigned_to: 'tech_b',
    tech_owner: 'tech_b',
    service_owner: 'svc_b',
    next_action_hint: '请关注最近进度',
    last_synced_at: '2026-05-14T10:00:00+08:00',
    task_created_at: '2026-05-13T09:30:00+08:00',
    task_finished_at: '',
    task_closed_at: '',
    sla_risk_level: 'normal',
    trigger_source_type: 'wechat_group_trigger',
  },
  {
    mapping_id: 'map-003',
    draft_no: 'DF-20260512-002',
    zentao_task_id: 12330,
    task_title: '配置-新增用户权限',
    customer_name: '某医院',
    platform_status: 'waiting_start',
    zentao_status: 'wait',
    assignee: '',
    assigned_to: '',
    tech_owner: 'tech_c',
    service_owner: 'svc_a',
    next_action_hint: '请提醒当前执行人接单或考虑改派',
    last_synced_at: '2026-05-14T09:00:00+08:00',
    task_created_at: '2026-05-12T09:00:00+08:00',
    task_finished_at: '',
    task_closed_at: '',
    sla_risk_level: 'danger',
    trigger_source_type: '',
  },
  {
    mapping_id: 'map-004',
    draft_no: 'DF-20260510-001',
    zentao_task_id: 12300,
    task_title: '排查-系统响应缓慢',
    customer_name: '某客户',
    platform_status: 'closed',
    zentao_status: 'closed',
    assignee: 'tech_c',
    assigned_to: 'tech_c',
    tech_owner: 'tech_c',
    service_owner: 'svc_c',
    next_action_hint: '',
    last_synced_at: '2026-05-13T18:00:00+08:00',
    task_created_at: '2026-05-10T10:00:00+08:00',
    task_finished_at: '2026-05-10T16:00:00+08:00',
    task_closed_at: '2026-05-10T17:00:00+08:00',
    sla_risk_level: 'normal',
    trigger_source_type: 'wechat_group_trigger',
  },
]

const mockTicketDetail: Record<string, TicketDetail> = {
  'map-001': {
    mapping_id: 'map-001',
    zentao_task_id: 12345,
    task_title: '排查-打印机E-500错误',
    is_out_of_warranty: false,
    customer_name: '某医院',
    project_name: '某医院HIS系统',
    platform_status: 'waiting_customer_confirm',
    zentao_status: 'done',
    status_label: '待客户确认',
    next_action_hint: '请同步客户确认',
    service_owner: 'svc_a',
    tech_owner: 'tech_a',
    assignee: 'tech_a',
    assigned_to: 'tech_a',
    human_assignee: 'tech_a',
    first_response_at: '2026-05-14T10:20:00+08:00',
    task_created_at: '2026-05-14T10:30:00+08:00',
    task_started_at: '2026-05-14T10:45:00+08:00',
    task_finished_at: '2026-05-14T11:20:00+08:00',
    customer_confirmed_at: '',
    task_closed_at: '',
    source_messages: [
      { message_id: 'msg-004', sender_name: '赵六', content_preview: '打印机报错，错误代码 E-500', send_time: '2026-05-14T09:50:00+08:00', media: [] },
    ],
    draft_summary: { draft_no: 'DF-20260514-003', task_title: '排查-打印机E-500错误', priority: 'P2' },
    open_sla_events: [
      { id: 'sla-001', rule_code: 'customer_confirm_timeout', rule_name: '客户确认超时', event_status: 'open' },
    ],
    task_status_events: [
      { status: 'create', note: '创建禅道任务', created_at: '2026-05-14T10:30:00+08:00' },
      { status: 'start', note: '开始处理', created_at: '2026-05-14T10:45:00+08:00' },
      { status: 'finish', note: '处理完成', created_at: '2026-05-14T11:20:00+08:00' },
    ],
    operator_actions: [
      { action: 'sync', label: '手动同步', enabled: true },
      { action: 'customer-confirm', label: '确认通过并关闭', enabled: true },
      { action: 'customer-return', label: '客户退回', enabled: true },
    ],
    source: {
      room_id: 'room:his-support',
      trigger_key: 'room:his-support:msg-004',
      trigger_message_id: 'wx-msg-trigger-004',
      trigger_message_summary: '打印机报错，错误代码 E-500',
      context_message_summary: '[合并转发] 打印问题讨论（3条消息）',
      forward_summary: '合并转发消息：门诊打印设备报错汇总',
    },
    confirm_script: '您好，打印机故障已处理完成。请确认是否恢复正常使用，如有问题请及时反馈。',
  },
  'map-002': {
    mapping_id: 'map-002',
    zentao_task_id: 12340,
    task_title: '修复-数据库连接超时',
    is_out_of_warranty: false,
    customer_name: '某客户',
    project_name: '某项目Portal',
    platform_status: 'doing',
    zentao_status: 'doing',
    status_label: '处理中',
    next_action_hint: '请关注最近进度',
    service_owner: 'svc_b',
    tech_owner: 'tech_b',
    assignee: 'tech_b',
    assigned_to: 'tech_b',
    human_assignee: 'tech_b',
    first_response_at: '2026-05-13T09:00:00+08:00',
    task_created_at: '2026-05-13T09:30:00+08:00',
    task_started_at: '2026-05-13T10:00:00+08:00',
    task_finished_at: '',
    customer_confirmed_at: '',
    task_closed_at: '',
    source_messages: [],
    draft_summary: null,
    open_sla_events: [],
    task_status_events: [],
    operator_actions: [
      { action: 'sync', label: '手动同步', enabled: true },
    ],
    source: {
      room_id: 'room:portal-support',
      trigger_key: 'room:portal-support:msg-002',
      trigger_message_id: 'wx-msg-trigger-002',
      trigger_message_summary: '登录时提示认证失败，请协助排查。',
      context_message_summary: '[合并转发] 用户反馈早上10点后登录不稳定',
    },
  },
}

// ============== Adapter: 后端 → 前端 UI model ==============
const STATUS_LABEL_MAP: Record<string, string> = {
  received: '已接收',
  draft_confirming: '草稿确认中',
  waiting_start: '待接单',
  doing: '处理中',
  waiting_customer_confirm: '待客户确认',
  closed: '已关闭',
  returned: '已退回',
}

function deriveStatusLabel(platformStatus: string): string {
  return STATUS_LABEL_MAP[platformStatus] || platformStatus
}

const TICKET_ACTION_LABELS: Record<string, string> = {
  sync: '手动同步',
  'customer-confirm': '确认通过并关闭',
  'customer-return': '客户退回继续处理',
}

function deriveOperatorActions(platformStatus: string): TicketDetail['operator_actions'] {
  const actions: TicketDetail['operator_actions'] = [
    { action: 'sync', label: '手动同步', enabled: true },
  ]
  if (platformStatus === 'waiting_customer_confirm') {
    actions.push(
      { action: 'customer-confirm', label: '确认通过并关闭', enabled: true },
      { action: 'customer-return', label: '客户退回', enabled: true },
    )
  } else {
    actions.push(
      { action: 'customer-confirm', label: '确认通过并关闭', enabled: false },
      { action: 'customer-return', label: '客户退回', enabled: false },
    )
  }
  return actions
}

function clampOperatorActionEnabled(action: string, enabled: boolean, platformStatus: string): boolean {
  if (action === 'customer-confirm' || action === 'customer-return') {
    return enabled && platformStatus === 'waiting_customer_confirm'
  }
  return enabled
}

function normalizeOperatorAction(item: unknown, platformStatus: string): TicketDetail['operator_actions'][number] | null {
  if (typeof item === 'string') {
    const action = item.trim()
    if (!action) return null
    return {
      action,
      label: TICKET_ACTION_LABELS[action] || action,
      enabled: clampOperatorActionEnabled(action, true, platformStatus),
    }
  }
  if (!item || typeof item !== 'object') return null
  const raw = item as Record<string, unknown>
  const action = String(raw.action ?? '').trim()
  if (!action) return null
  const enabled = typeof raw.enabled === 'boolean' ? raw.enabled : true
  return {
    action,
    label: String(raw.label ?? TICKET_ACTION_LABELS[action] ?? action),
    enabled: clampOperatorActionEnabled(action, enabled, platformStatus),
  }
}

function normalizeOperatorActions(raw: unknown, platformStatus: string): TicketDetail['operator_actions'] {
  const byAction = new Map(deriveOperatorActions(platformStatus).map((item) => [item.action, item]))
  if (Array.isArray(raw)) {
    raw.forEach((item) => {
      const action = normalizeOperatorAction(item, platformStatus)
      if (action) byAction.set(action.action, action)
    })
  }
  return Array.from(byAction.values())
}

export function normalizeTicketDetail(raw: unknown): TicketDetail {
  if (!raw || typeof raw !== 'object') throw new Error('Invalid ticket detail response')
  const r = raw as Record<string, unknown>
  const platformStatus = String(r.platform_status ?? '')

  return {
    mapping_id: String(r.mapping_id ?? ''),
    zentao_task_id: Number(r.zentao_task_id ?? 0),
    task_title: String(r.task_title ?? ''),
    is_out_of_warranty: Boolean(r.is_out_of_warranty ?? false),
    customer_name: String(r.customer_name ?? ''),
    project_name: String(r.project_name ?? ''),
    platform_status: platformStatus,
    zentao_status: String(r.zentao_status ?? ''),
    status_label: (r.status_label as string) || deriveStatusLabel(platformStatus),
    next_action_hint: String(r.next_action_hint ?? ''),
    service_owner: String(r.service_owner ?? ''),
    service_owner_name: String(r.service_owner_name ?? ''),
    tech_owner: String(r.tech_owner ?? ''),
    tech_owner_name: String(r.tech_owner_name ?? ''),
    assignee: String(r.assignee ?? r.assigned_to ?? ''),
    assignee_name: String(r.assignee_name ?? ''),
    assigned_to: String(r.assignee ?? r.assigned_to ?? ''),
    human_assignee: String(r.human_assignee ?? ''),
    human_assignee_name: String(r.human_assignee_name ?? ''),
    first_response_at: String(r.first_response_at ?? ''),
    task_created_at: String(r.task_created_at ?? ''),
    task_started_at: String(r.task_started_at ?? ''),
    task_finished_at: String(r.task_finished_at ?? ''),
    customer_confirmed_at: String(r.customer_confirmed_at ?? ''),
    task_closed_at: String(r.task_closed_at ?? ''),
    source_messages: Array.isArray(r.source_messages)
      ? (r.source_messages as Array<Record<string, unknown>>).map((msg) => ({
          message_id: String(msg.message_id ?? ''),
          sender_name: String(msg.sender_name ?? msg.sender ?? ''),
          content_preview: String(msg.content_preview ?? msg.content ?? ''),
          send_time: String(msg.send_time ?? ''),
          media: Array.isArray(msg.media) ? msg.media.map((item) => normalizeMessageMedia(item)) : [],
        }))
      : [],
    // 后端可能用 draft 或 draft_summary 字段
    draft_summary: (r.draft_summary ?? r.draft ?? null) as TicketDetail['draft_summary'] | null,
    open_sla_events: Array.isArray(r.open_sla_events) ? r.open_sla_events : [],
    // 后端字段 status_events[].event_type → 前端 task_status_events[].status
    task_status_events: (() => {
      const raw = r.status_events ?? r.task_status_events
      if (!Array.isArray(raw)) return []
      return (raw as Array<Record<string, unknown>>).map((evt) => ({
        // 后端返回 event_type，前端期望 status；兼容两种字段名
        status: String(evt.event_type ?? evt.status ?? ''),
        note: String(evt.note ?? ''),
        created_at: String(evt.created_at ?? ''),
      }))
    })(),
    customer_confirmation_attachments: Array.isArray(r.customer_confirmation_attachments)
      ? (r.customer_confirmation_attachments as Array<Record<string, unknown>>).map((item) => ({
          attachment_uid: String(item.attachment_uid ?? ''),
          original_name: String(item.original_name ?? ''),
          mime_type: item.mime_type == null ? null : String(item.mime_type),
          file_ext: item.file_ext == null ? null : String(item.file_ext),
          size_bytes: Number(item.size_bytes ?? 0),
          upload_status: String(item.upload_status ?? ''),
          zentao_file_url: item.zentao_file_url == null ? null : String(item.zentao_file_url),
          last_error: item.last_error == null ? null : String(item.last_error),
          created_at: item.created_at == null ? null : String(item.created_at),
          download_url: String(item.download_url ?? ''),
        }))
      : [],
    operator_actions: normalizeOperatorActions(r.operator_actions, platformStatus),
    // 微信群触发来源（可选字段）
    source: (r.source as TicketDetail['source']) ?? null,
    confirm_script: typeof r.confirm_script === 'string' ? r.confirm_script : undefined,
  }
}

// ============== API 方法 ==============
export async function fetchTickets(params: {
  page?: number
  page_size?: number
  platform_status?: string
  platform_status_group?: string
  assignee?: string
  tech_owner?: string
  keyword?: string
  person?: string
  created_from?: string
  created_to?: string
  sort_by?: string
  sort_order?: string
}): Promise<PaginatedData<TicketItem>> {
  if (isMockEnabled()) {
    await mockDelay()
    let filtered = [...mockTickets]
    if (params.platform_status) {
      filtered = filtered.filter((t) => t.platform_status === params.platform_status)
    } else if (params.platform_status_group === 'unfinished') {
      filtered = filtered.filter((t) => t.platform_status !== 'closed')
    }
    if (params.assignee) {
      filtered = filtered.filter((t) => (t.assignee || t.assigned_to) === params.assignee)
    }
    if (params.tech_owner) {
      filtered = filtered.filter((t) => t.tech_owner === params.tech_owner)
    }
    if (params.keyword) {
      const kw = params.keyword.toLowerCase()
      filtered = filtered.filter(
        (t) => t.task_title.toLowerCase().includes(kw) || t.customer_name.toLowerCase().includes(kw)
      )
    }
    if (params.person) {
      const kw = params.person.toLowerCase()
      filtered = filtered.filter((t) =>
        [t.assignee_name, t.assignee || t.assigned_to, t.tech_owner_name, t.tech_owner, t.service_owner_name, t.service_owner].some((value) => String(value || '').toLowerCase().includes(kw)),
      )
    }
    if (params.created_from) {
      filtered = filtered.filter((t) => String(t.task_created_at || '').slice(0, 10) >= params.created_from!)
    }
    if (params.created_to) {
      filtered = filtered.filter((t) => String(t.task_created_at || '').slice(0, 10) <= params.created_to!)
    }
    filtered = sortByParam(filtered, params.sort_by, params.sort_order, {
      created_at: 'task_created_at',
      task_created_at: 'task_created_at',
      updated_at: 'last_synced_at',
      last_synced_at: 'last_synced_at',
      task_finished_at: 'task_finished_at',
      task_closed_at: 'task_closed_at',
    })
    return paginate(filtered, params.page || 1, params.page_size || 20)
  }
  const { data } = await client.get<PaginatedData<TicketItem>>('/tickets', { params })
  return data
}

export async function fetchTicketStats(params: {
  platform_status?: string
  platform_status_group?: string
  assignee?: string
  tech_owner?: string
  keyword?: string
  person?: string
  created_from?: string
  created_to?: string
}): Promise<TicketStats> {
  if (isMockEnabled()) {
    const tickets = await fetchTickets({ ...params, page: 1, page_size: 1000 })
    const buildStats = (
      getOwner: (ticket: TicketItem) => string | undefined,
      getOwnerName: (ticket: TicketItem) => string | undefined,
    ) => {
      const byOwner = new Map<string, TicketStats['by_owner'][number]>()
      tickets.items.forEach((ticket) => {
        const owner = getOwner(ticket) || 'unassigned'
        const ownerName = getOwnerName(ticket) || ''
        const row = byOwner.get(owner) || {
          owner,
          owner_name: ownerName,
          total: 0,
          closed: 0,
          unfinished: 0,
          waiting_customer_confirm: 0,
          doing: 0,
          waiting_start: 0,
          returned: 0,
        }
        if (ownerName && !row.owner_name) row.owner_name = ownerName
        row.total += 1
        if (ticket.platform_status === 'closed') row.closed += 1
        else row.unfinished += 1
        if (['waiting_customer_confirm', 'doing', 'waiting_start', 'returned'].includes(ticket.platform_status)) {
          row[ticket.platform_status as 'waiting_customer_confirm' | 'doing' | 'waiting_start' | 'returned'] += 1
        }
        byOwner.set(owner, row)
      })
      return Array.from(byOwner.values()).sort((a, b) => b.total - a.total)
    }
    return {
      total: tickets.total,
      by_assignee: buildStats((ticket) => ticket.assignee || ticket.assigned_to, (ticket) => ticket.assignee_name),
      by_owner: buildStats((ticket) => ticket.tech_owner, (ticket) => ticket.tech_owner_name),
    }
  }
  const { data } = await client.get<TicketStats>('/tickets/stats', { params })
  return data
}

export async function exportTickets(params: {
  platform_status?: string
  platform_status_group?: string
  assignee?: string
  tech_owner?: string
  keyword?: string
  person?: string
  created_from?: string
  created_to?: string
}): Promise<void> {
  if (isMockEnabled()) {
    const tickets = await fetchTickets({ ...params, page: 1, page_size: 1000 })
    const blob = new Blob(['\ufeff', buildTicketExportCsv(tickets.items)], { type: 'text/csv;charset=utf-8' })
    downloadBlob(blob, `tickets-${new Date().toISOString().slice(0, 10)}.csv`)
    return
  }
  const response = await client.get<Blob>('/tickets/export', {
    params,
    responseType: 'blob',
  })
  const contentDisposition = response.headers['content-disposition']
  downloadBlob(response.data, extractDownloadFilename(typeof contentDisposition === 'string' ? contentDisposition : undefined))
}

export function exportTicketStatsCsv(stats: TicketStats): void {
  const headers = ['人员', '总工单', '已关闭', '未完结', '处理中', '待客户确认', '待接单', '已退回']
  const rows = [
    ['统计报表', `当前筛选共 ${stats.total} 单`],
    [],
    ['按当前执行人统计'],
    headers,
    ...(stats.by_assignee || []).map(formatStatsRow),
    [],
    ['按第一负责人统计'],
    headers,
    ...(stats.by_owner || []).map(formatStatsRow),
  ]
  const csv = rows.map((row) => row.map(formatCsvCell).join(',')).join('\r\n')
  const blob = new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8' })
  downloadBlob(blob, `ticket-stats-${new Date().toISOString().slice(0, 10)}.csv`)
}

function formatStatsRow(item: TicketStats['by_owner'][number]) {
  return [
    formatStatsOwner(item),
    item.total,
    item.closed,
    item.unfinished,
    item.doing,
    item.waiting_customer_confirm,
    item.waiting_start,
    item.returned,
  ]
}

function formatStatsOwner(item: TicketStats['by_owner'][number]) {
  const owner = item.owner || 'unassigned'
  const name = String(item.owner_name || '').trim()
  return name && name !== owner ? `${name}(${owner})` : owner
}

function buildTicketExportCsv(items: TicketItem[]) {
  const headers = ['工单ID', '禅道任务ID', '标题', '客户', '当前执行人', '第一负责人', '接待负责人', '平台状态', '禅道状态', '创建时间', '完成时间', '关闭时间']
  const rows = items.map((item) => [
    item.mapping_id,
    item.zentao_task_id,
    item.task_title,
    item.customer_name,
    item.assignee || item.assigned_to,
    item.tech_owner || '',
    item.service_owner || '',
    item.platform_status,
    item.zentao_status,
    item.task_created_at || '',
    item.task_finished_at || '',
    item.task_closed_at || '',
  ])
  return [headers, ...rows].map((row) => row.map(formatCsvCell).join(',')).join('\r\n')
}

function formatCsvCell(value: unknown) {
  const raw = String(value ?? '')
  const text = ['=', '+', '-', '@'].includes(raw.slice(0, 1)) ? `'${raw}` : raw
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

function extractDownloadFilename(contentDisposition?: string) {
  const quotedMatch = contentDisposition?.match(/filename="([^"]+)"/i)
  return quotedMatch?.[1] || `tickets-${new Date().toISOString().slice(0, 10)}.csv`
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

export async function fetchTicketDetail(mappingId: string): Promise<TicketDetail> {
  if (isMockEnabled()) {
    await mockDelay()
    const detail = mockTicketDetail[mappingId]
    if (!detail) {
      const item = mockTickets.find((t) => t.mapping_id === mappingId)
      if (!item) throw Object.assign(new Error('工单不存在'), { code: 'NOT_FOUND' })
      const fallback: TicketDetail = {
        mapping_id: item.mapping_id,
        zentao_task_id: item.zentao_task_id,
        task_title: item.task_title,
        is_out_of_warranty: item.is_out_of_warranty ?? false,
        customer_name: item.customer_name,
        project_name: '',
        platform_status: item.platform_status,
        zentao_status: item.zentao_status,
        status_label: item.platform_status,
        next_action_hint: item.next_action_hint,
        service_owner: '',
        tech_owner: '',
        assignee: item.assignee || item.assigned_to,
        assigned_to: item.assignee || item.assigned_to,
        human_assignee: '',
        first_response_at: '',
        task_created_at: '',
        task_started_at: '',
        task_finished_at: '',
        customer_confirmed_at: '',
        task_closed_at: '',
        source_messages: [],
        draft_summary: null,
        open_sla_events: [],
        task_status_events: [],
        operator_actions: [],
        source: null,
      }
      return fallback
    }
    return { ...detail }
  }
  const { data } = await client.get(`/tickets/${mappingId}`)
  return normalizeTicketDetail(data)
}

export async function syncTicket(taskId: number): Promise<void> {
  if (isMockEnabled()) {
    await mockDelay()
    return
  }
  await client.post(`/zentao/tasks/${taskId}/sync`, {})
}

export async function customerConfirm(mappingId: string, note: string, files: File[] = []): Promise<void> {
  if (isMockEnabled()) {
    await mockDelay()
    const t = mockTickets.find((t) => t.mapping_id === mappingId)
    if (t) t.platform_status = 'closed'
    return
  }
  const confirmedBy = useAuthStore.getState().actor || 'unknown'
  if (files.length > 0) {
    const formData = new FormData()
    formData.append('confirmed_by', confirmedBy)
    formData.append('confirmation_result', 'pass')
    formData.append('confirmation_note', note)
    files.forEach((file) => formData.append('files', file))
    await client.post(`/tickets/${mappingId}/customer-confirm-upload`, formData)
    return
  }
  await client.post(`/tickets/${mappingId}/customer-confirm`, {
    confirmed_by: confirmedBy,
    confirmation_result: 'pass',
    confirmation_note: note,
  })
}

/** 手动改派禅道 task */
export async function assignTask(mappingId: string, assignee: string): Promise<void> {
  if (isMockEnabled()) {
    await mockDelay()
    const t = mockTickets.find((t) => t.mapping_id === mappingId)
    if (t) {
      t.assignee = assignee
      t.assigned_to = assignee
    }
    return
  }
  // TODO: 等后端实现 POST /api/tickets/{mapping_id}/assign 后移除 mock
  await client.post(`/tickets/${mappingId}/assign`, { assigned_to: assignee })
}

export async function customerReturn(mappingId: string, reason: string): Promise<void> {
  if (isMockEnabled()) {
    await mockDelay()
    const t = mockTickets.find((t) => t.mapping_id === mappingId)
    if (t) t.platform_status = 'returned'
    return
  }
  const confirmedBy = useAuthStore.getState().actor || 'unknown'
  await client.post(`/tickets/${mappingId}/customer-return`, {
    confirmed_by: confirmedBy,
    return_reason: reason,
  })
}
